import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { Button } from '@/components/button'
import { MainLayout } from '@/components/layouts'
import { MainSidebar, MobileSidebar } from '@/components/sidebar'
import { useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { useClubManagementData } from '../hooks/useClubManagementData'
import { ClubManagementLayout } from '../components/ClubManagementLayout'

export const ClubManagement = () => {
  const { t } = useTranslation()
  const { isShowDropdown, isShowMenu } = useAppSelector(appSelector)
  const { data: managedClubs, totalClubs, isLoading, error } = useClubManagementData()
  const [searchParams, setSearchParams] = useSearchParams()

  const selectedClubId = (() => {
    const param = searchParams.get('club')
    if (!param) return null
    const parsed = Number(param)
    return Number.isFinite(parsed) ? parsed : null
  })()

  const handleSelectClub = useCallback(
    (clubId: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('club', String(clubId))
        // Fresh club starts clean — don't carry a tab/member from a previously open club.
        next.delete('tab')
        next.delete('member')
        return next
      })
    },
    [setSearchParams]
  )

  const handleDeselectClub = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('club')
      next.delete('tab')
      next.delete('member')
      return next
    })
  }, [setSearchParams])

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col gap-y-6 mb-20 lg:mb-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{t('club.management', 'Club Overview')}</h1>
              <div className="relative group">
                <span className="inline-flex items-center px-2 py-1 text-xs font-bold border border-yellow-400 text-yellow-400 bg-transparent rounded">
                  BETA
                </span>
                <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-900 text-white text-sm shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <p className="font-medium mb-1">Incubating Feature</p>
                  <p className="text-gray-300">
                    This feature is under active development. Please reach out to DUPR support if you experience any
                    bugs.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center py-12">
            <p className="text-white opacity-70">Loading your managed clubs...</p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex flex-col gap-y-6 mb-20 lg:mb-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{t('club.management', 'Club Overview')}</h1>
              <div className="relative group">
                <span className="inline-flex items-center px-2 py-1 text-xs font-bold border border-yellow-400 text-yellow-400 bg-transparent rounded">
                  BETA
                </span>
                <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-900 text-white text-sm shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <p className="font-medium mb-1">Incubating Feature</p>
                  <p className="text-gray-300">
                    This feature is under active development. Please reach out to DUPR support if you experience any
                    bugs.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-white opacity-70 mb-4">Error loading club management data</p>
              <Button
                className="bg-primary-500 text-white px-4 py-2 hover:bg-opacity-70"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      )
    }

    if (!managedClubs?.length) {
      return (
        <div className="flex flex-col gap-y-6 mb-20 lg:mb-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{t('club.management', 'Club Overview')}</h1>
              <div className="relative group">
                <span className="inline-flex items-center px-2 py-1 text-xs font-bold border border-yellow-400 text-yellow-400 bg-transparent rounded">
                  BETA
                </span>
                <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-900 text-white text-sm shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <p className="font-medium mb-1">Incubating Feature</p>
                  <p className="text-gray-300">
                    This feature is under active development. Please reach out to DUPR support if you experience any
                    bugs.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-white opacity-70 mb-2">No clubs to manage</p>
              <p className="text-white opacity-50 text-sm">You don't have DIRECTOR or ORGANIZER roles in any clubs</p>
            </div>
          </div>
        </div>
      )
    }

    return (
      <ClubManagementLayout
        clubs={managedClubs}
        totalClubs={totalClubs}
        selectedClubId={selectedClubId}
        onSelectClub={handleSelectClub}
        onDeselectClub={handleDeselectClub}
      />
    )
  }

  return (
    <>
      {!isShowDropdown && <MobileSidebar />}
      <MainLayout>
        <MainSidebar />
        <div
          className={`transition-all duration-500 ease-in-out px-4 lg:px-8
            ${isShowMenu && 'lg:ml-56 2xl:ml-72'}`}
        >
          <div className="mx-auto max-w-full xl:max-w-[68vw] 2xl:max-w-[62vw]">{renderContent()}</div>
        </div>
      </MainLayout>
    </>
  )
}
